import { logger } from 'common/services';

import { createBackgroundImageError } from '../../utils/backgroundImageError';
import {
  CUSTOM_BACKGROUNDS_CHAR_BUDGET,
  IMAGE_KEY_PREFIX,
  INDEX_KEY,
  MAX_CUSTOM_BACKGROUNDS,
  imageKey,
  thumbnailKey,
} from './constants';

import type { CustomBackgroundEntry, NewCustomBackground } from './types';

/**
 * Only the fields the feature cannot work without.
 *
 * **Never add optional metadata to this guard.** A rejected entry is dropped from the index, and
 * `sweepOrphanedCustomBackgrounds` then deletes the image it named — so a missing or malformed
 * optional field would cost the user their picture, permanently, on the next boot. Anything
 * optional is repaired by `withValidDimensions` instead.
 */
const isEntry = (value: unknown): value is CustomBackgroundEntry =>
  !!value &&
  typeof value === 'object' &&
  typeof (value as CustomBackgroundEntry).id === 'string' &&
  typeof (value as CustomBackgroundEntry).name === 'string' &&
  typeof (value as CustomBackgroundEntry).createdAt === 'number' &&
  typeof (value as CustomBackgroundEntry).chars === 'number';

const isDimension = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value > 0;

/**
 * Source dimensions survive only as a complete, sane pair. Absent, half-written or non-numeric — an
 * older entry, a hand-edited index, a future version — all collapse to "unknown", which reads the
 * same as a background stored before we recorded them. The entry itself always survives.
 *
 * `JSON.stringify` drops `undefined` keys, so a repaired entry re-serialises clean on the next
 * write rather than carrying a `null` around.
 */
const withValidDimensions = (entry: CustomBackgroundEntry): CustomBackgroundEntry =>
  isDimension(entry.sourceWidth) && isDimension(entry.sourceHeight)
    ? entry
    : { ...entry, sourceWidth: undefined, sourceHeight: undefined };

/**
 * The stored index, oldest first.
 *
 * Anything unparseable reads as an empty list rather than throwing: this runs at slice init, so a
 * corrupt value would otherwise take the whole app down on boot for the sake of a wallpaper.
 */
export const readCustomBackgroundIndex = (): CustomBackgroundEntry[] => {
  try {
    const raw = localStorage.getItem(INDEX_KEY);
    if (!raw) return [];

    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];

    return parsed
      .filter(isEntry)
      .map(withValidDimensions)
      .sort((a, b) => a.createdAt - b.createdAt);
  } catch (error) {
    logger.error('[customBackgroundStorage] Could not read the custom background index', error);

    return [];
  }
};

const writeIndex = (entries: CustomBackgroundEntry[]) => localStorage.setItem(INDEX_KEY, JSON.stringify(entries));

/** Total characters the stored backgrounds occupy, index included. */
/**
 * Measures the index by re-serialising it rather than reading the raw key again: `getItem` throws
 * outright where storage is blocked (Safari private browsing), and this is called from
 * `writeCustomBackground` *before* its try/catch, so a raw read would escape untyped. Going through
 * `readCustomBackgroundIndex` inherits its guard, and sizing what we'd write is the honest number.
 */
export const getUsedChars = () => {
  const index = readCustomBackgroundIndex();

  return index.reduce((total, { chars }) => total + chars, 0) + JSON.stringify(index).length;
};

const readKey = (key: string) => localStorage.getItem(key) ?? undefined;

export const readCustomBackgroundImage = (id: string) => readKey(imageKey(id));
export const readCustomBackgroundThumbnail = (id: string) => readKey(thumbnailKey(id));

const isQuotaError = (error: unknown) =>
  error instanceof Error && (error.name === 'QuotaExceededError' || error.name === 'NS_ERROR_DOM_QUOTA_REACHED');

/**
 * Persists one background, enforcing both budget guards first.
 *
 * **Order matters: image, then thumbnail, then the index.** Three `setItem` calls are not a
 * transaction, so if a later one throws we roll the earlier ones back. Writing the index *last* means
 * a hard crash mid-write leaves orphaned image keys but never a dangling index entry — a state
 * `sweepOrphanedCustomBackgrounds` can clean up, rather than one the picker would render as a broken
 * tile. Orphans matter more than usual here because the budget they leak is shared with auth.
 *
 * @throws `BackgroundImageError` with `limitReached`, `quotaExceeded` or `storageFailed`.
 */
export const writeCustomBackground = (
  entry: NewCustomBackground,
  imageDataUrl: string,
  thumbnailDataUrl: string
): CustomBackgroundEntry => {
  const index = readCustomBackgroundIndex();

  if (index.length >= MAX_CUSTOM_BACKGROUNDS) throw createBackgroundImageError('limitReached');

  const chars = imageDataUrl.length + thumbnailDataUrl.length;
  const stored: CustomBackgroundEntry = { ...entry, chars };

  if (getUsedChars() + chars > CUSTOM_BACKGROUNDS_CHAR_BUDGET) throw createBackgroundImageError('quotaExceeded');

  try {
    localStorage.setItem(imageKey(entry.id), imageDataUrl);
    localStorage.setItem(thumbnailKey(entry.id), thumbnailDataUrl);
    writeIndex([...index, stored]);
  } catch (error) {
    localStorage.removeItem(imageKey(entry.id));
    localStorage.removeItem(thumbnailKey(entry.id));

    if (isQuotaError(error)) throw createBackgroundImageError('quotaExceeded');

    logger.error('[customBackgroundStorage] Could not save the custom background', error);
    throw createBackgroundImageError('storageFailed');
  }

  return stored;
};

/** Removes a background and its bytes. Always safe — freeing space can't hit the quota. */
export const removeCustomBackground = (id: string) => {
  localStorage.removeItem(imageKey(id));
  localStorage.removeItem(thumbnailKey(id));
  writeIndex(readCustomBackgroundIndex().filter((entry) => entry.id !== id));
};

/**
 * Deletes image keys with no matching index entry — the residue of a tab closed mid-write.
 *
 * Called once when the custom-backgrounds slice initialises. Without it those bytes are unreachable
 * but still counted against the origin's shared budget, so they'd slowly squeeze out auth and device
 * preferences with no way for a user to notice or clear them.
 */
export const sweepOrphanedCustomBackgrounds = () => {
  try {
    const liveKeys = new Set(readCustomBackgroundIndex().flatMap(({ id }) => [imageKey(id), thumbnailKey(id)]));

    // `length`/`key(i)` rather than `Object.keys`: it's the Web Storage API proper, so it behaves the
    // same on a real `Storage` and on the in-memory stand-in `setupTests` installs. Collect the whole
    // list before removing anything, since deleting re-indexes what's left.
    const orphans = Array.from({ length: localStorage.length }, (_, index) => localStorage.key(index)).filter(
      (key): key is string => !!key && key.startsWith(IMAGE_KEY_PREFIX) && !liveKeys.has(key)
    );

    orphans.forEach((key) => localStorage.removeItem(key));

    if (orphans.length) {
      logger.warn(`[customBackgroundStorage] Removed ${orphans.length} orphaned custom background key(s)`);
    }
  } catch (error) {
    logger.error('[customBackgroundStorage] Could not sweep orphaned custom backgrounds', error);
  }
};
